import type {
  IPitcherClient,
  RelativeWorkspacePath,
} from "@codesandbox/pitcher-client";
import type { OpenArgs } from "component-inspector/dist/types";
import {
  relativePathToResource,
  type Environment,
} from "environment-interface";
import { ITextModelService, StandaloneServices } from "vscode/services";

import { convertDiffToMonaco } from "./diff";
import { getPrompt } from "./prompts";

const N_LINE_CONTEXT = 30;

export async function suggestCode(
  openFileArgs: OpenArgs,
  relativePath: RelativeWorkspacePath,
  pitcher: IPitcherClient,
  environment: Environment,
  disableInspector: () => void,
  sendNotification: (message: string) => void,
) {
  if (!pitcher.permissions.ai?.raw) {
    return;
  }
  const file = await pitcher.clients.file.openFileByPath(relativePath);
  const content = file.object.content;
  if (typeof content !== "string") {
    return;
  }

  const vscodeApi = await environment.monacoEditor.getExtensionApi();

  const inputBox = vscodeApi.window.createInputBox();
  inputBox.title = "Describe the changes you'd like to make to this component";
  inputBox.placeholder = "Make it rainbow coloured";
  inputBox.onDidAccept(async () => {
    inputBox.busy = true;
    inputBox.enabled = false;

    const lines = content.split("\n");

    const start = Math.max(0, openFileArgs.lineNumber - N_LINE_CONTEXT);
    const end = Math.min(
      lines.length - 1,
      openFileArgs.lineNumber + N_LINE_CONTEXT,
    );

    const contextCode = lines.slice(start, end).join("\n");

    const alteredContextCode = lines
      .slice(start, end)
      .map((line, index) => {
        if (index === openFileArgs.lineNumber - start - 1) {
          return `$${line}`;
        } else {
          return line;
        }
      })
      .join("\n");

    const aiPrompt = getPrompt(
      relativePath,
      alteredContextCode,
      inputBox.value,
    );

    const result = await pitcher.clients.ai?.raw({
      model: "gpt-3.5-turbo",
      messages: aiPrompt,
      max_tokens: 1024,
      temperature: 0.1,
    });

    if (result.reply) {
      const monacoChanges = convertDiffToMonaco(
        result.reply,
        start,
        contextCode,
      );

      if (monacoChanges.length === 0) {
        sendNotification(
          "We couldn't find relevant changes to make to the code",
        );
      }

      const model = await StandaloneServices.get(
        ITextModelService,
      ).createModelReference(relativePathToResource(relativePath));
      model.object.textEditorModel.pushStackElement();
      model.object.textEditorModel.pushEditOperations(
        [],
        monacoChanges,
        () => null,
      );

      file.object.save();
      disableInspector();
      inputBox.dispose();
    }
  });
  inputBox.show();
}
